import { useState } from 'react'

import { ActStrip } from './ActStrip.jsx'
import { LogItControl } from './LogItControl.jsx'
import { SeriesChecklist } from './SeriesChecklist.jsx'
import { DEBOUNCE_MS, MIN_QUERY_LENGTH, useTitleSearch } from './useTitleSearch.js'
import { PosterSlot } from './WatchPanel.jsx'
import { KIND_ADD_TITLE, addCommand, logTargetForSearchHit, useWatchRequest } from './watchRequests.js'

// 🔴 V2-6f2 §2.1: `useTitleSearch` (and `MIN_QUERY_LENGTH`/`DEBOUNCE_MS`,
// which its own setTimeout and this file's own render both need) MOVED to
// `useTitleSearch.js`, unchanged -- re-exported below so this module's own
// public surface (`import { MIN_QUERY_LENGTH } from './AddPanel.jsx'`, were
// anyone to write it) is untouched. A pure move: nothing about how a member
// searches changed, only where the code that does it lives. See that file's
// own header for the debounce rule it now carries.
export { DEBOUNCE_MS, MIN_QUERY_LENGTH }

// V2-4c: "Add a show" — `docs/design-briefs/watch/v2/D2D Watch v2.dc.html`'s
// own "Add a show" section (its markup, not only `HANDOFF.md`'s prose — the
// brief's own instruction, after V2-8b's first round lost three features by
// following the prose alone).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 V2-4b2: "ADD TO THE GROUP'S LIST" IS NOW A REAL BUTTON
// ═══════════════════════════════════════════════════════════════════════════
//
// `watch.add_title` (ADR-0009 D5, `cogs/watch/requests.py::
// _evaluate_add_title`) lists a title into the guild's configured watch
// channel (`/watch admin channel`, P6-287) -- created first, if the crew
// has never seen it at all. `AddToListControl`, below, asks for it exactly
// like `LogItControl` asks for `watch.log` — the SAME `ActStrip`/
// `useWatchRequest` machinery, no second implementation. The mono
// `/watch add query:…` fallback (`addCommand`) is UNCHANGED from before
// this kind existed; it now serves only the UNREACHABLE state
// (RECONCILIATION item 11: "on unavailable, the controls are replaced by
// the equivalent Discord command in mono"), never the everyday path.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 "ALREADY ON THE LIST" / "YOU LOGGED IT" ARE NOT DRAWN — A FINDING, NOT
//    AN OVERSIGHT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's own mock (`results()` in the prototype) can answer "is this
// search hit already on the group's list" because its fake data is
// omniscient — every title the mock knows about carries both a provider id
// AND the bot's own catalog id in the SAME object. The real system cannot:
// `GET /api/watch/search` is a stateless TMDB/AniList lookup with no
// knowledge of the bot's catalog at all (`docs/reports/V2-4a.md` §1 — a
// separate service, deliberately), and nothing published anywhere (the
// watch projection, the archive, the search response) carries a provider id
// on a catalog row for the other side to match against. There is no way to
// answer "is this TMDB hit already tracked" from data this slice can reach
// without a new, unbuilt piece of backend surface. So every result offers
// "Log it as watched" unconditionally, rather than guessing. See
// `docs/reports/V2-4c.md` for the full account.
//
/** "TMDB TV, 2022" / "TMDB Movie, 2024" / "AniList, year unknown" — the
 * EXACT format `cogs/watch/helpers.py::source_option_description` builds
 * for the bot's own `/watch add` picker (spec §3: "each labeled with its
 * source and year"), so a title reads identically on the site and in
 * Discord. Deliberately NOT `watch.js::sourceLabel` — that function
 * answers a different question (a LISTED row's bare "TMDB"/"ANILIST", used
 * nowhere near a TMDB TV/Movie distinction) for a different caller. */
export function searchHitLabel(hit) {
  const source =
    hit.provider === 'tmdb' ? (hit.kind === 'movie' ? 'TMDB Movie' : 'TMDB TV') : 'AniList'
  const year = typeof hit.year === 'number' && Number.isFinite(hit.year) ? String(hit.year) : 'year unknown'
  return `${source}, ${year}`
}

/** The honest per-provider line — brief §2: "when TMDB or AniList failed,
 * or has no key, say so plainly, and still show the other's results."
 * `null` when that provider is fine, so the caller renders nothing for it. */
export function providerFlagText(name, info) {
  if (!info || info.ok) return null
  return info.reason === 'no-key'
    ? `${name} is not set up on this server yet.`
    : `${name} isn’t answering right now.`
}

/** V2-4b2: "Add to the group's list" — `watch.add_title`, `ActStrip`'s own
 * asked/applied/refused/expired states, and the mono `/watch add` fallback
 * on Unreachable. `target` is the SAME provider-qualified reference
 * `LogItControl` sends for `watch.log` (`logTargetForSearchHit`, reused —
 * never a second target-builder); `query` is the raw search text, for the
 * Unreachable fallback's own honest command (`addCommand`).
 *
 * No confirm-after-applied: the SAME reasoning `LogItControl`'s own header
 * already states for `watch.log` applies here unchanged — a search result
 * has no fresh projection on this page to reconcile against
 * (`/api/watch/search` is a stateless, disjoint service,
 * `docs/reports/V2-4a.md` §1), and `useWatchRequest`'s own poll already
 * does not report APPLIED until the bot's applier has genuinely listed the
 * title — showing "Added to the group's list" at that point is the truth,
 * not a guess. */
function AddToListControl({ target, query, testId }) {
  const request = useWatchRequest()

  const press = () => {
    request.ask({ kind: KIND_ADD_TITLE, target, payload: {} })
  }

  if (request.status) {
    return (
      <ActStrip
        request={request}
        testId={testId}
        doneText="Added to the group's list"
        command={addCommand(query)}
        onRetry={() => press()}
      />
    )
  }

  return (
    <button
      type="button"
      className="d2d-addpanel__addbtn"
      data-testid={`${testId}-button`}
      onClick={press}
    >
      Add to the group's list
    </button>
  )
}

/** One search result — `docs/design-briefs/watch/v2/D2D Watch v2.dc.html`
 * lines 119-180, verbatim structure: a poster, the title, the source/year
 * label, the add action, and Log it. `target` is `null` only for a hit
 * shape this build does not recognise (an unrecognised provider, or a
 * TMDB kind that is neither `tv` nor `movie`) — permanently unavailable
 * for that one case, since there is no honest target to send either
 * request against. */
function SearchResultRow({ hit }) {
  const target = logTargetForSearchHit(hit)
  return (
    <div className="d2d-addpanel__result" data-testid="search-result">
      <PosterSlot subscription={{ poster_url: hit.poster }} size="search" />
      <div className="d2d-addpanel__resultbody">
        <div className="d2d-addpanel__resulttitle">{hit.title}</div>
        <div className="d2d-micro d2d-addpanel__resultmeta">{searchHitLabel(hit)}</div>
        {target ? (
          <AddToListControl target={target} query={hit.title} testId="search-add" />
        ) : (
          <p className="d2d-micro d2d-addpanel__addunavail" data-testid="search-add-unavailable">
            Add it in Discord — <code className="d2d-actstrip__cmd">{addCommand(hit.title)}</code>
          </p>
        )}
        {target ? <LogItControl target={target} title={hit.title} testId="search-log" /> : null}
        {/* V2-6f2 §2.2: "On every AniList search result, a 'Log the whole
            series' control. TMDB results don't get it -- a TMDB show is
            already one title with all its seasons." */}
        {hit.provider === 'anilist' ? <SeriesChecklist hit={hit} testId="search-series" /> : null}
      </div>
    </div>
  )
}

/** The Add panel itself — `aria-label="Add a show"`, the section's own name
 * (RECONCILIATION item 0). Mounted only while the caller's own toggle is
 * open, and only for a signed-in member (`WatchPanel.jsx`'s own
 * `showAddBtn`/`addShown` gate, mirrored at the call site). */
export function AddPanel() {
  const [query, setQuery] = useState('')
  const search = useTitleSearch(query)
  const trimmed = query.trim()

  const tmdbFlag = providerFlagText('TMDB', search.providers?.tmdb)
  const anilistFlag = providerFlagText('AniList', search.providers?.anilist)

  return (
    <section aria-label="Add a show" className="d2d-addpanel" data-testid="add-panel">
      <label className="d2d-addpanel__searchlabel">
        <span className="d2d-micro d2d-addpanel__searchkicker">Search TV, anime and movies</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="A title…"
          className="d2d-addpanel__searchinput"
          data-testid="add-panel-search-input"
        />
      </label>

      {tmdbFlag ? (
        <p className="d2d-micro d2d-addpanel__flag" data-testid="search-flag-tmdb">
          {tmdbFlag}
        </p>
      ) : null}
      {anilistFlag ? (
        <p className="d2d-micro d2d-addpanel__flag" data-testid="search-flag-anilist">
          {anilistFlag}
        </p>
      ) : null}

      {search.status === 'limited' ? (
        <p className="d2d-addpanel__notice" role="alert" data-testid="search-rate-limited">
          {search.message}
        </p>
      ) : null}
      {search.status === 'error' ? (
        <p className="d2d-addpanel__notice" role="alert" data-testid="search-error">
          {search.message}
        </p>
      ) : null}

      {trimmed.length < MIN_QUERY_LENGTH ? (
        <p className="d2d-addpanel__hint" data-testid="search-hint">
          Type at least two letters.
        </p>
      ) : search.status === 'ready' && search.results.length === 0 ? (
        <p className="d2d-addpanel__hint" data-testid="search-empty">
          {`Nothing found on TMDB or AniList for “${trimmed}”.`}
        </p>
      ) : null}

      {search.results.map((hit) => (
        <SearchResultRow key={`${hit.provider}:${hit.kind}:${hit.id}`} hit={hit} />
      ))}
    </section>
  )
}

export default AddPanel
